import React, { useState, useContext } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { AuthContext } from "../../AuthProvider";
import { FaFileUpload, FaTimesCircle, FaFileImport } from "react-icons/fa";
import { toast } from "react-toastify";

const ImportLeadsModal = ({ onClose, onImport }) => {
  const { user } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Map Fields, 3: Review
  const [mappings, setMappings] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    address: "",
    cityStateZip: "",
    focusArea: "",
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csv = event.target.result;
        const lines = csv.split("\n");

        // Extract headers
        const headerLine = lines[0].trim();
        const extractedHeaders = headerLine
          .split(",")
          .map((h) => h.trim().replace(/"/g, ""));
        setHeaders(extractedHeaders);

        // Parse data
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line) {
            const values = parseCsvLine(line);
            if (values.length === extractedHeaders.length) {
              const row = {};
              extractedHeaders.forEach((header, index) => {
                row[header] = values[index];
              });
              rows.push(row);
            }
          }
        }

        setData(rows);

        // Try to auto-map fields
        const autoMap = {};
        extractedHeaders.forEach((header) => {
          const lowerHeader = header.toLowerCase();

          // Check for name field
          if (
            lowerHeader.includes("name") &&
            !lowerHeader.includes("first") &&
            !lowerHeader.includes("last")
          ) {
            autoMap.fullName = header;
          } else if (
            lowerHeader === "first name" ||
            lowerHeader.includes("firstname")
          ) {
            autoMap.firstName = header;
          } else if (
            lowerHeader === "last name" ||
            lowerHeader.includes("lastname")
          ) {
            autoMap.lastName = header;
          }

          // Check for email field - look for exact "email address" match or includes "email"
          if (
            lowerHeader === "email address" ||
            lowerHeader.includes("email")
          ) {
            autoMap.email = header;
          }

          if (
            lowerHeader.includes("company") ||
            lowerHeader.includes("organization")
          )
            autoMap.company = header;
          if (
            lowerHeader.includes("title") ||
            lowerHeader.includes("role") ||
            lowerHeader.includes("position")
          )
            autoMap.role = header;
          if (
            lowerHeader.includes("phone") ||
            lowerHeader.includes("mobile") ||
            lowerHeader.includes("cell")
          )
            autoMap.phone = header;
          if (lowerHeader.includes("address")) autoMap.address = header;
          if (
            lowerHeader.includes("city") ||
            lowerHeader.includes("state") ||
            lowerHeader.includes("zip")
          )
            autoMap.cityStateZip = header;
          if (lowerHeader.includes("focus") || lowerHeader.includes("crop"))
            autoMap.focusArea = header;
        });

        setMappings(autoMap);
        setStep(2);
      } catch (err) {
        console.error("Error parsing CSV:", err);
        setError(
          "Failed to parse CSV file. Please make sure it's a valid CSV format."
        );
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Error reading file");
      setLoading(false);
    };

    reader.readAsText(file);
  };

  const parseCsvLine = (line) => {
    const result = [];
    let inQuotes = false;
    let currentValue = "";

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(currentValue.replace(/"/g, "").trim());
        currentValue = "";
      } else {
        currentValue += char;
      }
    }

    // Add the last value
    result.push(currentValue.replace(/"/g, "").trim());
    return result;
  };

  const handleMappingChange = (field, value) => {
    if (field === "fullName" && value) {
      // If selecting fullName, clear firstName and lastName
      setMappings((prev) => ({
        ...prev,
        [field]: value,
        firstName: "",
        lastName: "",
      }));
    } else if ((field === "firstName" || field === "lastName") && value) {
      // If selecting firstName or lastName, clear fullName
      setMappings((prev) => ({
        ...prev,
        [field]: value,
        ...(field === "firstName"
          ? {}
          : prev.firstName
          ? { fullName: "" }
          : {}),
        ...(field === "lastName" ? {} : prev.lastName ? { fullName: "" } : {}),
      }));
    } else {
      // Normal field update
      setMappings((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleReview = () => {
    // Validate required fields
    if (mappings.fullName) {
      // If using full name field, that's sufficient
      if (!mappings.email) {
        setError("Full Name and Email fields must be mapped");
        return;
      }
    } else if (!mappings.firstName || !mappings.lastName || !mappings.email) {
      setError("First Name, Last Name, and Email fields must be mapped");
      return;
    }

    setStep(3);
  };

  const handleImport = async () => {
    if (!user) {
      setError("You must be logged in to import leads");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const importedLeads = [];
      let newLeadsCount = 0;
      let updatedLeadsCount = 0;
      let skippedCount = 0;

      console.log("Starting import with mappings:", mappings);
      console.log("Data to import:", data);

      const importPromises = data.map(async (row) => {
        // Parse name fields
        let firstName = "";
        let lastName = "";

        if (mappings.fullName) {
          // Split full name into first and last name
          const fullName = row[mappings.fullName] || "";
          const nameParts = fullName.trim().split(" ");
          if (nameParts.length > 1) {
            firstName = nameParts[0];
            lastName = nameParts.slice(1).join(" ");
          } else {
            firstName = fullName;
            lastName = ""; // No last name available
          }
        } else {
          firstName = mappings.firstName ? row[mappings.firstName] || "" : "";
          lastName = mappings.lastName ? row[mappings.lastName] || "" : "";
        }

        // Get email from the mapping - could be "Email" or "Email Address"
        const emailValue = mappings.email ? row[mappings.email] || "" : "";

        // Create lead object based on mappings with date tracking
        const now = new Date();
        const lead = {
          firstName: firstName,
          lastName: lastName,
          name: `${firstName} ${lastName}`.trim(),
          company: mappings.company ? row[mappings.company] || "" : "",
          role: mappings.role ? row[mappings.role] || "" : "",
          email: emailValue,
          phone: mappings.phone ? row[mappings.phone] || "" : "",
          address: mappings.address ? row[mappings.address] || "" : "",
          cityStateZip: mappings.cityStateZip
            ? row[mappings.cityStateZip] || ""
            : "",
          focusArea: mappings.focusArea ? row[mappings.focusArea] || "" : "",

          // Date tracking
          updatedAt: now,
          lastEditedAt: now,
          importedAt: now,
          source: "csv_import_ui",
        };

        console.log("Processing lead:", lead);

        // Skip leads without email
        if (!lead.email) {
          console.log("Skipping lead with no email:", lead);
          skippedCount++;
          return null;
        }

        // Use the email directly as the document ID - Firestore supports special characters in doc IDs
        const docId = lead.email.toLowerCase();

        // Reference to the lead document using email-based ID
        const leadDocRef = doc(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "collections",
          "leads",
          docId
        );

        // Check if the document already exists
        const docSnap = await getDoc(leadDocRef);
        let isNew = !docSnap.exists();

        if (isNew) {
          // It's a new lead
          lead.createdAt = now;
          lead.editHistory = [
            {
              action: "imported",
              timestamp: now,
              source: "csv_import_ui",
              by: user.email || user.uid,
            },
          ];
          newLeadsCount++;
        } else {
          // It's an existing lead - keep the original creation date and update edit history
          const existingData = docSnap.data();
          lead.createdAt = existingData.createdAt || now;
          lead.editHistory = [
            ...(existingData.editHistory || []),
            {
              action: "updated_via_import",
              timestamp: now,
              source: "csv_import_ui",
              by: user.email || user.uid,
            },
          ];
          updatedLeadsCount++;
        }

        // Set the document with merge true to update existing or create new
        await setDoc(leadDocRef, lead, { merge: true });

        return {
          id: docId,
          ...lead,
          isNew: isNew,
        };
      });

      const results = await Promise.all(importPromises);
      const validResults = results.filter((result) => result !== null);

      // Show success message with counts
      toast.success(
        `Import successful: ${newLeadsCount} new leads added, ${updatedLeadsCount} leads updated, ${skippedCount} skipped`
      );

      if (onImport) onImport(validResults);
      onClose();
    } catch (err) {
      console.error("Error importing leads:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderUploadStep = () => {
    return (
      <div className="p-6">
        <p className="text-gray-600 mb-4">
          Upload a CSV file containing your leads. The first row should contain
          column headers.
        </p>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <FaFileUpload className="mx-auto text-gray-400 text-5xl mb-4" />
          <p className="mb-4">
            Drag & drop a CSV file here, or click to browse
          </p>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 cursor-pointer"
          >
            Select CSV File
          </label>
        </div>

        {file && (
          <div className="mt-4 p-3 bg-gray-50 rounded flex justify-between items-center">
            <div className="text-sm">
              <p className="font-medium">{file.name}</p>
              <p className="text-gray-500">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <button
              onClick={() => setFile(null)}
              className="text-red-500 hover:text-red-700"
            >
              <FaTimesCircle />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMapFieldsStep = () => {
    return (
      <div className="p-6">
        <p className="text-gray-600 mb-4">
          Map the columns from your CSV file to the appropriate lead fields.
        </p>

        <div className="space-y-4">
          <div>
            <label
              className={`block text-sm font-medium ${
                mappings.firstName || mappings.lastName
                  ? "text-gray-400"
                  : "text-gray-700"
              } mb-1`}
            >
              Full Name * (Required)
            </label>
            <select
              value={mappings.fullName}
              onChange={(e) => handleMappingChange("fullName", e.target.value)}
              className={`w-full p-2 border rounded ${
                mappings.firstName || mappings.lastName
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-purple-500"
              }`}
              disabled={!!(mappings.firstName || mappings.lastName)}
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Will be automatically split into First and Last Name
            </p>
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium text-gray-500 mb-3">
              -- OR provide separate First/Last name fields --
            </p>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${
                mappings.fullName ? "text-gray-400" : "text-gray-700"
              } mb-1`}
            >
              First Name * (If not using Full Name)
            </label>
            <select
              value={mappings.firstName}
              onChange={(e) => handleMappingChange("firstName", e.target.value)}
              className={`w-full p-2 border rounded ${
                mappings.fullName
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-purple-500"
              }`}
              disabled={!!mappings.fullName}
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${
                mappings.fullName ? "text-gray-400" : "text-gray-700"
              } mb-1`}
            >
              Last Name * (If not using Full Name)
            </label>
            <select
              value={mappings.lastName}
              onChange={(e) => handleMappingChange("lastName", e.target.value)}
              className={`w-full p-2 border rounded ${
                mappings.fullName
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-purple-500"
              }`}
              disabled={!!mappings.fullName}
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email * (Required)
            </label>
            <select
              name="email"
              value={mappings.email}
              onChange={(e) => handleMappingChange("email", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={mappings.role}
              onChange={(e) => handleMappingChange("role", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <select
              value={mappings.company}
              onChange={(e) => handleMappingChange("company", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <select
              value={mappings.phone}
              onChange={(e) => handleMappingChange("phone", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <select
              value={mappings.address}
              onChange={(e) => handleMappingChange("address", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City/State/Zip
            </label>
            <select
              value={mappings.cityStateZip}
              onChange={(e) =>
                handleMappingChange("cityStateZip", e.target.value)
              }
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Focus Area(s)
            </label>
            <select
              value={mappings.focusArea}
              onChange={(e) => handleMappingChange("focusArea", e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a column</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    // Count valid leads to display
    const validLeads = data.filter((row) => {
      const hasFullName = mappings.fullName && row[mappings.fullName];
      const hasEmail = mappings.email && row[mappings.email];
      const hasFirstOrLastName =
        (mappings.firstName && row[mappings.firstName]) ||
        (mappings.lastName && row[mappings.lastName]);

      return hasEmail && (hasFullName || hasFirstOrLastName);
    });

    return (
      <div className="p-6">
        <p className="text-gray-600 mb-4">
          Review your leads before importing. A total of {validLeads.length}{" "}
          leads will be imported.
        </p>

        <div className="border rounded overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.slice(0, 5).map((row, index) => {
                // Get name to display in preview table
                let displayName = "";
                if (mappings.fullName) {
                  displayName = row[mappings.fullName];
                } else {
                  displayName = `${row[mappings.firstName] || ""} ${
                    row[mappings.lastName] || ""
                  }`.trim();
                }

                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {displayName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {mappings.company ? row[mappings.company] : ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {mappings.role ? row[mappings.role] : ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {mappings.email ? row[mappings.email] : ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {mappings.phone ? row[mappings.phone] : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data.length > 5 && (
          <p className="text-gray-500 text-sm mt-2">
            Showing 5 of {validLeads.length} leads
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Import Leads</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimesCircle size={24} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 1
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              1
            </div>
            <div
              className={`flex-1 h-1 mx-2 ${
                step >= 2 ? "bg-purple-600" : "bg-gray-200"
              }`}
            ></div>
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 2
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              2
            </div>
            <div
              className={`flex-1 h-1 mx-2 ${
                step >= 3 ? "bg-purple-600" : "bg-gray-200"
              }`}
            ></div>
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 3
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              3
            </div>
          </div>
          <div className="flex justify-between mt-1 text-xs">
            <span>Upload CSV</span>
            <span>Map Fields</span>
            <span>Review & Import</span>
          </div>
        </div>

        {/* Content based on current step */}
        {loading ? (
          <div className="p-6 flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {step === 1 && renderUploadStep()}
            {step === 2 && renderMapFieldsStep()}
            {step === 3 && renderReviewStep()}
          </>
        )}

        {/* Error message */}
        {error && (
          <div className="mx-6 my-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded">
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div className="p-6 border-t flex justify-between">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Back
            </button>
          ) : (
            <div></div> // Placeholder to maintain flexbox alignment
          )}

          {step < 3 ? (
            <button
              onClick={step === 1 ? () => {} : handleReview}
              disabled={step === 1 || loading}
              className={`px-4 py-2 rounded ${
                step === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              {step === 1 ? "Next" : "Review"}
            </button>
          ) : (
            <button
              onClick={handleImport}
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
            >
              <FaFileImport className="mr-2" />
              {loading ? "Importing..." : "Import Leads"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportLeadsModal;
